/**
 * ToolsTab — Agent tools inventory for MCP migration readiness tracking.
 */

import React, { useState, useEffect, useRef } from 'react';
import {
  RefreshCw, CheckCircle, AlertTriangle, FileText, Settings,
  Loader2, Globe, ChevronDown, KeyRound, Eye, Database, MessageSquare, Link2
} from 'lucide-react';
import type { AgentToolsGroup, SharedTool, IndexConfig, AgentPromptsData } from './types';
import { getAgentColor, getAgentDisplayName } from './constants';

interface ToolsTabProps {
  agentTools: AgentToolsGroup[];
  sharedTools: SharedTool[];
  indexes: IndexConfig[];
  agentPrompts: Record<string, AgentPromptsData>;
  onRefresh: () => void;
}

const ToolsTab: React.FC<ToolsTabProps> = ({ agentTools, sharedTools, indexes, agentPrompts, onRefresh }) => {
  const [expandedAgent, setExpandedAgent] = useState<string | null>(null);
  const [expandedTool, setExpandedTool] = useState<string | null>(null);
  const [showSharedTools, setShowSharedTools] = useState(false);
  const [expandedPrompt, setExpandedPrompt] = useState<string | null>(null);
  const [showSubAgents, setShowSubAgents] = useState<Record<string, boolean>>({});
  const [highlightedToolInPrompt, setHighlightedToolInPrompt] = useState<string | null>(null);
  const promptRefs = useRef<Record<string, HTMLPreElement | null>>({});
  
  // State for pending scroll (to scroll after DOM updates)
  const [pendingScrollToPrompt, setPendingScrollToPrompt] = useState<string | null>(null);
  
  // Effect to scroll to prompt after it's rendered
  useEffect(() => {
    if (pendingScrollToPrompt) {
      // Wait for DOM to update
      const timeoutId = setTimeout(() => {
        const promptEl = promptRefs.current[pendingScrollToPrompt];
        if (promptEl) {
          promptEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
        setPendingScrollToPrompt(null);
      }, 100);
      return () => clearTimeout(timeoutId);
    }
  }, [pendingScrollToPrompt, expandedPrompt]);
  
  // Clear highlight after 3 seconds
  useEffect(() => {
    if (highlightedToolInPrompt) {
      const timeoutId = setTimeout(() => setHighlightedToolInPrompt(null), 3000);
      return () => clearTimeout(timeoutId);
    }
  }, [highlightedToolInPrompt]);
  
  // Function to find tool in prompt and scroll to it
  const showToolInPrompt = (agentId: string, toolName: string) => {
    const agentPromptData = agentPrompts[agentId];
    if (!agentPromptData) {
      // Still expand the agent so user can see the prompts section
      setExpandedAgent(agentId);
      return;
    }
    
    // Find which prompt variable mentions this tool
    const prompts = agentPromptData.prompts || {};
    let foundPromptKey: string | null = null;
    
    for (const [varName, promptData] of Object.entries(prompts)) {
      const promptValue = promptData.value || '';
      const idx = promptValue.toLowerCase().indexOf(toolName.toLowerCase());
      if (idx !== -1) {
        foundPromptKey = `${agentId}-prompt-${varName}`;
        break;
      }
    }
    
    // If tool not found in any prompt, just expand to first prompt
    if (!foundPromptKey && Object.keys(prompts).length > 0) {
      const firstPromptKey = Object.keys(prompts)[0];
      foundPromptKey = `${agentId}-prompt-${firstPromptKey}`;
    }
    
    if (foundPromptKey) {
      // Expand the agent and prompt
      setExpandedAgent(agentId);
      setExpandedPrompt(foundPromptKey);
      setHighlightedToolInPrompt(toolName);
      
      // Queue scroll for after DOM update
      setPendingScrollToPrompt(foundPromptKey);
    } else {
      // Just expand the agent
      setExpandedAgent(agentId);
    }
  };
  
  // Helper to highlight tool mentions in prompt text
  const highlightToolInText = (text: string, toolName: string | null) => {
    if (!toolName) return text;
    
    const regex = new RegExp(`(${toolName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
    const parts = text.split(regex);
    
    return parts.map((part, i) => 
      part.toLowerCase() === toolName.toLowerCase() 
        ? <mark key={i} className="tool-highlight">{part}</mark>
        : part
    );
  };
  
  // Find index info for a given index name
  const findIndex = (indexName?: string) => {
    if (!indexName) return null;
    return indexes.find(idx => 
      idx.id.toLowerCase().includes(indexName.toLowerCase()) ||
      indexName.toLowerCase().includes(idx.id.toLowerCase())
    );
  };
  
  // Get icon for resource type
  const getResourceIcon = (type?: string) => {
    switch (type) {
      case 'index': return <Database size={12} />;
      case 'blob': return <FileText size={12} />;
      case 'api': return <Globe size={12} />;
      case 'mcp': return <Settings size={12} />;
      case 'keyvault': return <KeyRound size={12} />;
      default: return null;
    }
  };
  
  // Get color for resource type
  const getResourceColor = (type?: string) => {
    switch (type) {
      case 'index': return '#10b981';   // Green
      case 'blob': return '#3b82f6';    // Blue
      case 'api': return '#f59e0b';     // Amber
      case 'mcp': return '#10b981';     // Green
      case 'keyvault': return '#ef4444'; // Red
      default: return '#6f747b';        // Gray
    }
  };
  
  const totalTools = agentTools.reduce((sum, a) => sum + a.toolCount, 0);
  const mcpReadyCount = agentTools.reduce((sum, a) => sum + (a.mcpReadyCount || 0), 0);
  
  return (
    <div className="idx-tools-tab">
      {/* Header with stats */}
      <div className="idx-tools-header">
        <div className="idx-tools-stats">
          <div className="idx-stat-card">
            <Settings size={24} />
            <div className="idx-stat-info">
              <span className="idx-stat-value">{totalTools}</span>
              <span className="idx-stat-label">Total Tools</span>
            </div>
          </div>
          <div className="idx-stat-card">
            <Database size={24} />
            <div className="idx-stat-info">
              <span className="idx-stat-value">{agentTools.length}</span>
              <span className="idx-stat-label">Agents</span>
            </div>
          </div>
          <div className="idx-stat-card mcp">
            <Globe size={24} />
            <div className="idx-stat-info">
              <span className="idx-stat-value">{mcpReadyCount}/{totalTools}</span>
              <span className="idx-stat-label">MCP Ready</span>
            </div>
          </div>
        </div>
        <button className="idx-refresh-btn" onClick={onRefresh}>
          <RefreshCw size={18} />
          Refresh
        </button>
      </div>
      
      {/* MCP Migration Banner */}
      <div className="idx-mcp-banner">
        <AlertTriangle size={20} />
        <div>
          <strong>MCP Migration Planning</strong>
          <p>These tools are candidates for migration to Model Context Protocol (MCP) servers. 
             Tools marked as "MCP Ready" can be migrated with minimal changes.</p>
        </div>
      </div>
      
      {/* Shared Tools Section */}
      {sharedTools.length > 0 && (
        <div className="idx-shared-tools-section">
          <div 
            className="idx-shared-tools-header"
            onClick={() => setShowSharedTools(!showSharedTools)}
          >
            <div className="idx-shared-tools-info">
              <Link2 size={18} className="idx-shared-icon" />
              <h3>Shared Tools</h3>
              <span className="idx-shared-count">{sharedTools.length} tool{sharedTools.length !== 1 ? 's' : ''} used across multiple agents</span>
            </div>
            <ChevronDown size={20} className={`idx-expand-icon ${showSharedTools ? 'rotated' : ''}`} />
          </div>
          {showSharedTools && (
            <div className="idx-shared-tools-content">
              {sharedTools.map((tool, idx) => (
                <div key={idx} className="idx-shared-tool-card">
                  <div className="idx-shared-tool-header">
                    <code className="idx-tool-fn">{tool.name}</code>
                  </div>
                  <p className="idx-shared-tool-desc">{tool.description}</p>
                  
                  {/* Show actual prompt snippet content */}
                  {tool.promptSnippet && (
                    <div className="idx-shared-snippet-content">
                      <div className="snippet-section">
                        <h6>When to Use:</h6>
                        <pre>{tool.promptSnippet.whenToUse}</pre>
                      </div>
                      {tool.promptSnippet.whenNotToUse && (
                        <div className="snippet-section warning">
                          <h6>When NOT to Use:</h6>
                          <pre>{tool.promptSnippet.whenNotToUse}</pre>
                        </div>
                      )}
                      <div className="snippet-section">
                        <h6>How to Use:</h6>
                        <pre>{tool.promptSnippet.howToUse}</pre>
                      </div>
                      {tool.promptSnippet.examples && tool.promptSnippet.examples.length > 0 && (
                        <div className="snippet-section examples">
                          <h6>Examples:</h6>
                          {tool.promptSnippet.examples.map((ex, ei) => (
                            <div key={ei} className="snippet-example">
                              <span className="example-scenario">{ex.scenario}</span>
                              <div className="example-io">
                                <div className="example-input">
                                  <label>Input:</label>
                                  <code>{ex.input}</code>
                                </div>
                                {ex.output && (
                                  <div className="example-output">
                                    <label>Output:</label>
                                    <code>{ex.output.length > 100 ? `${ex.output.slice(0, 100)}...` : ex.output}</code>
                                  </div>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                  
                  <div className="idx-shared-tool-used-by">
                    <span>Used by:</span>
                    {tool.usedBy.map((agentId, i) => (
                      <span key={i} className="idx-agent-mini-tag" style={{ 
                        backgroundColor: `color-mix(in srgb, ${getAgentColor(agentId)} 15%, white)`,
                        color: getAgentColor(agentId)
                      }}>
                        {getAgentDisplayName(agentId)}
                      </span>
                    ))}
                  </div>
                  <div className="idx-shared-tool-file">
                    <code>{tool.file}</code>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
      
      {/* Agent Groups */}
      <div className="idx-tools-list">
        {agentTools.map(agent => {
          const isExpanded = expandedAgent === agent.agentId;
          const agentColor = getAgentColor(agent.displayName) || '#6f747b';
          
          return (
            <div 
              key={agent.agentId} 
              className={`idx-agent-tools-card ${isExpanded ? 'expanded' : ''}`}
              style={{ '--agent-color': agentColor } as React.CSSProperties}
            >
              {/* Agent Header */}
              <div 
                className="idx-agent-tools-header"
                onClick={() => setExpandedAgent(isExpanded ? null : agent.agentId)}
              >
                <div className="idx-agent-tools-info">
                  <span 
                    className="idx-agent-color-dot"
                    style={{ backgroundColor: agentColor }}
                  />
                  <h3>{agent.displayName}</h3>
                  <span className="idx-tool-count">{agent.toolCount} tools</span>
                  {agent.mcpReadyCount > 0 && (
                    <span className="idx-mcp-count">
                      <CheckCircle size={12} />
                      {agent.mcpReadyCount} MCP
                    </span>
                  )}
                  {agent.resources && agent.resources.length > 0 && (
                    <span className="idx-resources-count">
                      {agent.resources.filter(r => r.type === 'index').length > 0 && (
                        <span className="resource-type-badge" style={{ color: getResourceColor('index') }}>
                          <Database size={12} />
                          {agent.resources.filter(r => r.type === 'index').length}
                        </span>
                      )}
                      {agent.resources.filter(r => r.type === 'mcp').length > 0 && (
                        <span className="resource-type-badge" style={{ color: getResourceColor('mcp') }}>
                          <Settings size={12} />
                          {agent.resources.filter(r => r.type === 'mcp').length}
                        </span>
                      )}
                      {agent.resources.filter(r => r.type === 'api').length > 0 && (
                        <span className="resource-type-badge" style={{ color: getResourceColor('api') }}>
                          <Globe size={12} />
                          {agent.resources.filter(r => r.type === 'api').length}
                        </span>
                      )}
                      {agent.resources.filter(r => r.type === 'blob').length > 0 && (
                        <span className="resource-type-badge" style={{ color: getResourceColor('blob') }}>
                          <FileText size={12} />
                          {agent.resources.filter(r => r.type === 'blob').length}
                        </span>
                      )}
                    </span>
                  )}
                </div>
                <ChevronDown 
                  size={20} 
                  className={`idx-expand-icon ${isExpanded ? 'rotated' : ''}`}
                />
              </div>
              
              {/* Expanded Tool List */}
              {isExpanded && (
                <div className="idx-tools-details">
                  {/* Resources Used */}
                  {agent.resources && agent.resources.length > 0 && (
                    <div className="idx-resources-used">
                      <h4>Resources</h4>
                      <div className="idx-resources-tags">
                        {agent.resources.map((resource, ri) => {
                          const indexInfo = resource.type === 'index' ? findIndex(resource.name) : null;
                          return (
                            <span 
                              key={ri}
                              className={`idx-resource-tag ${resource.type}`}
                              style={{ 
                                borderColor: getResourceColor(resource.type),
                                backgroundColor: `color-mix(in srgb, ${getResourceColor(resource.type)} 10%, white)`
                              }}
                            >
                              {getResourceIcon(resource.type)}
                              <span className="resource-name">{resource.name}</span>
                              {indexInfo?.stats?.documentCount && (
                                <span className="doc-count">
                                  ({indexInfo.stats.documentCount.toLocaleString()} docs)
                                </span>
                              )}
                            </span>
                          );
                        })}
                      </div>
                    </div>
                  )}
                  
                  {/* Tools List with Expandable Details */}
                  <div className="idx-tools-list-detailed">
                    {agent.tools.map((tool, idx) => {
                      const toolKey = `${agent.agentId}-${tool.name}`;
                      const isToolExpanded = expandedTool === toolKey;
                      
                      return (
                        <div key={idx} className={`idx-tool-row ${isToolExpanded ? 'expanded' : ''}`}>
                          {/* Tool Summary Row */}
                          <div 
                            className="idx-tool-summary"
                            onClick={() => setExpandedTool(isToolExpanded ? null : toolKey)}
                          >
                            <div className="idx-tool-main">
                              <ChevronDown 
                                size={14} 
                                className={`tool-expand-icon ${isToolExpanded ? 'rotated' : ''}`}
                              />
                              <code className="idx-tool-fn">{tool.name}</code>
                              {tool.mcpReady && (
                                <span className="mcp-badge">
                                  <CheckCircle size={10} />
                                  MCP
                                </span>
                              )}
                            </div>
                            <div className="idx-tool-meta">
                              <span className="idx-tool-short-desc">{tool.description}</span>
                              {tool.resource && (
                                <span 
                                  className="idx-resource-mini-tag"
                                  style={{ color: getResourceColor(tool.resource.type) }}
                                >
                                  {getResourceIcon(tool.resource.type)}
                                  <span className="resource-type">{tool.resource.type}</span>
                                </span>
                              )}
                            </div>
                          </div>
                          
                          {/* Expanded Tool Details */}
                          {isToolExpanded && (
                            <div className="idx-tool-details">
                              {/* Docstring */}
                              {tool.docstring && (
                                <div className="idx-tool-docstring">
                                  <h5>Documentation</h5>
                                  <pre>{tool.docstring}</pre>
                                </div>
                              )}
                              
                              {/* Parameters */}
                              {tool.parameters && tool.parameters.length > 0 && (
                                <div className="idx-tool-params">
                                  <h5>Parameters</h5>
                                  <table className="params-table">
                                    <thead>
                                      <tr>
                                        <th>Name</th>
                                        <th>Type</th>
                                        <th>Required</th>
                                        <th>Description</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {tool.parameters.map((param, pi) => (
                                        <tr key={pi}>
                                          <td><code>{param.name}</code></td>
                                          <td><code className="param-type">{param.type}</code></td>
                                          <td>
                                            {param.required ? (
                                              <CheckCircle size={12} className="required" />
                                            ) : (
                                              <span className="optional">optional</span>
                                            )}
                                          </td>
                                          <td>{param.description}</td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                              )}
                              
                              {/* Return Type */}
                              {tool.returnType && (
                                <div className="idx-tool-return">
                                  <h5>Returns</h5>
                                  <code>{tool.returnType}</code>
                                </div>
                              )}
                              
                              {/* Resource Info */}
                              {tool.resource && (
                                <div className="idx-tool-resource-detail">
                                  <h5>Resource</h5>
                                  <span 
                                    className="idx-resource-tag-large"
                                    style={{ 
                                      borderColor: getResourceColor(tool.resource.type),
                                      backgroundColor: `color-mix(in srgb, ${getResourceColor(tool.resource.type)} 10%, white)`
                                    }}
                                  >
                                    {getResourceIcon(tool.resource.type)}
                                    <span className="resource-type-label">{tool.resource.type.toUpperCase()}</span>
                                    <span className="resource-name">{tool.resource.name}</span>
                                  </span>
                                </div>
                              )}
                              
                              {/* Prompt Snippet - from Python prompt_snippets.py */}
                              {tool.promptSnippet && (
                                <div className="idx-tool-prompt-snippet">
                                  <h5>
                                    <MessageSquare size={14} />
                                    Agent Prompt Snippet
                                    <span className="snippet-source">
                                      from {tool.promptSnippet.sourceFile}
                                    </span>
                                  </h5>
                                  
                                  <div className="snippet-section">
                                    <h6>When to Use:</h6>
                                    <pre>{tool.promptSnippet.whenToUse}</pre>
                                  </div>
                                  
                                  {tool.promptSnippet.whenNotToUse && (
                                    <div className="snippet-section warning">
                                      <h6>When NOT to Use:</h6>
                                      <pre>{tool.promptSnippet.whenNotToUse}</pre>
                                    </div>
                                  )}
                                  
                                  <div className="snippet-section">
                                    <h6>How to Use:</h6>
                                    <pre>{tool.promptSnippet.howToUse}</pre>
                                  </div>
                                  
                                  {tool.promptSnippet.examples && tool.promptSnippet.examples.length > 0 && (
                                    <div className="snippet-section examples">
                                      <h6>Examples:</h6>
                                      {tool.promptSnippet.examples.map((ex, ei) => (
                                        <div key={ei} className="snippet-example">
                                          <span className="example-scenario">{ex.scenario}</span>
                                          <div className="example-io">
                                            <div className="example-input">
                                              <label>Input:</label>
                                              <code>{ex.input}</code>
                                            </div>
                                            {ex.output && (
                                              <div className="example-output">
                                                <label>Output:</label>
                                                <code>{ex.output.length > 100 ? `${ex.output.slice(0, 100)}...` : ex.output}</code>
                                              </div>
                                            )}
                                          </div>
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              )}
                              
                              {/* Show in Prompt Button */}
                              <button 
                                className="idx-show-in-prompt-btn"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (agentPrompts[agent.agentId]) {
                                    showToolInPrompt(agent.agentId, tool.name);
                                  } else {
                                    // Scroll to prompt section even if not loaded
                                    setExpandedAgent(agent.agentId);
                                    setExpandedPrompt(`${agent.agentId}-prompt-scroll`);
                                  }
                                }}
                                title={agentPrompts[agent.agentId] ? 'Jump to this tool in the system prompt' : 'Prompts loading...'}
                              >
                                <Eye size={14} />
                                Show in System Prompt
                              </button>
                              
                              {/* File Location */}
                              <div className="idx-tool-file-info">
                                <h5>Source</h5>
                                <code>{tool.file}</code>
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                  
                  {/* System Prompt Section - Extracted from source files */}
                  {agentPrompts[agent.agentId] && (
                    <div className="idx-agent-prompt-section">
                      <h4>
                        <FileText size={16} />
                        System Prompts
                        <span className="prompt-count">
                          {Object.keys(agentPrompts[agent.agentId].prompts || {}).length} prompt(s)
                        </span>
                      </h4>
                      
                      {/* Main Agent Prompts */}
                      {Object.entries(agentPrompts[agent.agentId].prompts || {}).map(([varName, promptData]) => {
                        const promptKey = `${agent.agentId}-prompt-${varName}`;
                        const isPromptExpanded = expandedPrompt === promptKey;
                        
                        return (
                          <div key={varName} className="idx-prompt-card">
                            <div 
                              className="idx-prompt-header"
                              onClick={() => setExpandedPrompt(isPromptExpanded ? null : promptKey)}
                            >
                              <ChevronDown 
                                size={14} 
                                className={`prompt-expand-icon ${isPromptExpanded ? 'rotated' : ''}`}
                              />
                              <code className="prompt-var-name">{varName}</code>
                              <span className="prompt-stats">
                                {promptData.line_count} lines · {promptData.char_count.toLocaleString()} chars
                              </span>
                              <span className="prompt-source">
                                {promptData.source_file}
                              </span>
                            </div>
                            
                            {isPromptExpanded && (
                              <div className="idx-prompt-content">
                                <pre ref={el => { promptRefs.current[promptKey] = el; }}>
                                  {highlightedToolInPrompt 
                                    ? highlightToolInText(promptData.value, highlightedToolInPrompt)
                                    : promptData.value
                                  }
                                </pre>
                              </div>
                            )}
                          </div>
                        );
                      })}
                      
                      {/* Sub-Agents (Product agent has these) */}
                      {agentPrompts[agent.agentId].sub_agents && 
                       Object.keys(agentPrompts[agent.agentId].sub_agents || {}).length > 0 && (
                        <div className="idx-sub-agents-section">
                          <h5>
                            <Settings size={14} />
                            Sub-Agents
                          </h5>
                          
                          {Object.entries(agentPrompts[agent.agentId].sub_agents || {}).map(([subAgentId, subAgentData]) => {
                            const isSubExpanded = showSubAgents[subAgentId] || false;
                            
                            return (
                              <div key={subAgentId} className="idx-sub-agent-card">
                                <div 
                                  className="idx-sub-agent-header"
                                  onClick={() => setShowSubAgents(prev => ({ 
                                    ...prev, 
                                    [subAgentId]: !prev[subAgentId] 
                                  }))}
                                >
                                  <ChevronDown 
                                    size={14} 
                                    className={`sub-agent-expand-icon ${isSubExpanded ? 'rotated' : ''}`}
                                  />
                                  <span className="sub-agent-name">{subAgentId}</span>
                                  <span className="sub-agent-prompt-count">
                                    {Object.keys(subAgentData.prompts || {}).length} prompt(s)
                                  </span>
                                </div>
                                
                                {isSubExpanded && (
                                  <div className="idx-sub-agent-prompts">
                                    {Object.entries(subAgentData.prompts || {}).map(([varName, promptData]) => {
                                      const promptKey = `${agent.agentId}-${subAgentId}-${varName}`;
                                      const isPromptExpanded = expandedPrompt === promptKey;
                                      
                                      return (
                                        <div key={varName} className="idx-prompt-card sub">
                                          <div 
                                            className="idx-prompt-header"
                                            onClick={() => setExpandedPrompt(isPromptExpanded ? null : promptKey)}
                                          >
                                            <ChevronDown 
                                              size={12} 
                                              className={`prompt-expand-icon ${isPromptExpanded ? 'rotated' : ''}`}
                                            />
                                            <code className="prompt-var-name">{varName}</code>
                                            <span className="prompt-stats">
                                              {promptData.line_count} lines
                                            </span>
                                          </div>
                                          
                                          {isPromptExpanded && (
                                            <div className="idx-prompt-content">
                                              <pre>{promptData.value}</pre>
                                            </div>
                                          )}
                                        </div>
                                      );
                                    })}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export { ToolsTab };
